import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, CheckCircle, Info, AlertTriangle, X } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '../ui/alert'
import { useAlert } from '../../contexts/AlertContext'

export const GlobalAlert: React.FC = () => {
  const { alerts, removeAlert } = useAlert()
  const { t } = useTranslation()

  if (alerts.length === 0) return null

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'error':
        return <AlertCircle className="h-4 w-4" />
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />
      case 'info':
        return <Info className="h-4 w-4 text-blue-600" />
      default:
        return <AlertCircle className="h-4 w-4" />
    }
  }

  const getAlertClassName = (type: string) => {
    switch (type) {
      case 'success':
        return 'border-green-200 bg-green-50'
      case 'error':
        return 'bg-white border-red-200' // White background for error
      case 'warning':
        return 'border-yellow-200 bg-yellow-50'
      case 'info':
        return 'border-blue-200 bg-blue-50'
      default:
        return ''
    }
  }

  const getDefaultTitle = (type: string) => {
    switch (type) {
      case 'success':
        return t('common:success')
      case 'error':
        return t('common:error')
      case 'warning':
        return t('common:warning')
      case 'info':
        return t('common:info')
      default:
        return ''
    }
  }

  const getTextColorClass = (type: string) => {
    switch (type) {
      case 'success':
        return 'text-green-800'
      case 'warning':
        return 'text-yellow-800'
      case 'info':
        return 'text-blue-800'
      default:
        return ''
    }
  }

  const getDescriptionColorClass = (type: string) => {
    switch (type) {
      case 'success':
        return 'text-green-700'
      case 'warning':
        return 'text-yellow-700'
      case 'info':
        return 'text-blue-700'
      default:
        return ''
    }
  }

  const getCloseButtonColorClass = (type: string) => {
    switch (type) {
      case 'success':
        return 'text-green-600'
      case 'warning':
        return 'text-yellow-600'
      case 'info':
        return 'text-blue-600'
      default:
        return ''
    }
  }

  return (
    <div
      data-global-alert
      className="pointer-events-none fixed top-20 left-1/2 transform -translate-x-1/2 z-[1000] max-w-md w-full px-4 space-y-2"
    >
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className={`pointer-events-auto transform transition-all duration-300 ease-in-out ${
            alert.isRemoving
              ? '-translate-y-2 opacity-0 scale-95'
              : 'translate-y-0 opacity-100 scale-100'
          }`}
        >
          <Alert
            variant={alert.type === 'error' ? 'destructive' : 'default'}
            className={`relative shadow-lg pr-12 ${getAlertClassName(alert.type)}`}
          >
            {getAlertIcon(alert.type)}
            <AlertTitle className={getTextColorClass(alert.type)}>
              {alert.title || getDefaultTitle(alert.type)}
            </AlertTitle>
            <AlertDescription
              className={`pr-2 ${getDescriptionColorClass(alert.type)}`}
            >
              {alert.message}
            </AlertDescription>
            <button
              className={`absolute top-2 right-2 opacity-70 hover:opacity-100 transition-opacity ${getCloseButtonColorClass(alert.type)}`}
              onClick={() => removeAlert(alert.id)}
              aria-label={t('common:close', { defaultValue: 'Close' })}
            >
              <X className="h-4 w-4" />
            </button>
          </Alert>
        </div>
      ))}
    </div>
  )
}
